/**
 * DisclosuresModal: the "Good to know" notes as a parchment popup.
 *
 *   - "firstRun": a new player sees it after the welcome screen, before the
 *                 tour ("Start the tour"); a returning player sees it once
 *                 per disclosure version. The button writes the versioned
 *                 ack flag; Escape and backdrop clicks are inert so it is
 *                 actually read.
 *   - "review":   opened from Settings → Good to Know. No flag write.
 *                 Escape and backdrop click both close.
 *
 * Visual language matches HowToPlayTutorial (same backdrop, parchment
 * surface, gold accents, framer-motion enter/exit) so the two popups
 * feel like part of the same family on first launch.
 */

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { markDisclosuresAcked } from "@/lib/disclosures";
import DisclosureNotes from "@/components/DisclosureNotes";

interface DisclosuresModalProps {
  mode: "firstRun" | "review";
  /** Button label in firstRun mode (default "Got it"). */
  ctaLabel?: string;
  /** Called when the modal closes. In firstRun mode the ack flag is
   *  already written by the time this fires. */
  onDismiss: () => void;
}

export default function DisclosuresModal({
  mode,
  ctaLabel = "Got it",
  onDismiss,
}: DisclosuresModalProps) {
  const isMobile = useIsMobile();
  useBodyScrollLock(true);

  const handleAck = () => {
    if (mode === "firstRun") markDisclosuresAcked();
    onDismiss();
  };

  // In review mode, Escape and backdrop close. In firstRun mode the
  // player presses "Got it": Escape is a no-op and backdrop clicks are
  // inert, so the notes can't be dismissed by accident unseen.
  useEffect(() => {
    if (mode !== "review") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, onDismiss]);

  const innerContent = (
    <div className="relative">
      {mode === "review" && (
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 z-20 px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center gap-1.5 font-display text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="px-6 sm:px-8 pt-6 pb-5">
        <p className="font-display text-[10px] uppercase tracking-[0.3em] text-gold-dim">
          {mode === "firstRun" ? "Before you play" : "About the game"}
        </p>
        <h2 className="mt-1.5 mb-5 font-display text-xl text-foreground gold-text-glow">
          Good to know
        </h2>
        <DisclosureNotes />
      </div>

      <div
        className="h-px mx-6"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(43, 50%, 35% / 0.25), transparent)",
        }}
      />

      <div className="px-5 py-3 flex items-center justify-end">
        <button
          onClick={handleAck}
          className="px-5 py-2.5 min-h-[44px] font-display text-[11px] uppercase tracking-[0.2em] rounded-sm bg-primary/90 text-primary-foreground border border-primary hover:bg-primary transition-colors"
        >
          {mode === "firstRun" ? ctaLabel : "Close"}
        </button>
      </div>
    </div>
  );

  // In firstRun mode the backdrop is inert; in review mode it dismisses.
  const onBackdropClick = mode === "review" ? onDismiss : undefined;

  return (
    <AnimatePresence>
      <motion.div
        key="disclosures-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[9995] bg-black/70 backdrop-blur-sm"
        onClick={onBackdropClick}
      />

      {isMobile ? (
        <motion.div
          key="disclosures-sheet"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 280 }}
          className="fixed bottom-0 left-0 right-0 z-[9996] max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] overflow-y-auto rounded-t-lg overflow-hidden pb-safe"
          style={{
            background: "var(--surface-parchment)",
            border: "1px solid hsl(43, 50%, 35% / 0.25)",
            boxShadow: "0 -8px 30px hsl(30, 20%, 4% / 0.6)",
          }}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-border/60" />
          </div>
          <div
            className="h-[2px]"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(43, 72%, 55% / 0.4), transparent)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              backgroundSize: "128px 128px",
            }}
          />
          {innerContent}
          <div
            className="h-[2px]"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(43, 72%, 55% / 0.15), transparent)",
            }}
          />
        </motion.div>
      ) : (
        <motion.div
          key="disclosures-modal"
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[9996] m-auto w-[720px] max-w-[calc(100vw-32px)] h-fit max-h-[calc(100vh-48px)] supports-[height:100dvh]:max-h-[calc(100dvh-48px)] overflow-y-auto rounded-sm"
          style={{
            background: "var(--surface-parchment)",
            border: "1px solid hsl(43, 50%, 35% / 0.25)",
            boxShadow:
              "0 20px 60px hsl(30, 20%, 4% / 0.8), inset 0 1px 0 hsl(43, 60%, 55% / 0.08)",
          }}
        >
          <div
            className="h-[2px] rounded-t-sm"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(43, 72%, 55% / 0.4), transparent)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay rounded-sm"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              backgroundSize: "128px 128px",
            }}
          />
          {innerContent}
          <div
            className="h-[2px] rounded-b-sm"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(43, 72%, 55% / 0.15), transparent)",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
